// src/encounter/wrap-up-actions-header.tsx
// WrapUpActionsHeader — the E2 Wrap-Up section header row (PROD-1588 wave S7, Samer's
// wrap-up frame: artifacts/parity-baseline/2026-07-20-encounter/wrap-up-frame.png).
// One row: [green ✓ identity tile] "Wrap-Up" title · THREE TIMED ACTION clusters
// (EPRESCRIPTION · SEND ORDERS (n) · SIGN NOTE — each a clock/✓ + uppercase label over a
// row of icon actions) · trailing handwritten-notes/comment.
//
// SOURCE-TRUTH CORRECTION (2026-08-06, Andrew: "do as flutter", live Flutter screenshots):
// this row previously rendered FIVE clusters and a mic. Three of those had no counterpart
// in `wrap_up_billing_screen.dart` and were removed:
//   · MARK AS SEEN   — `markAsSeen` is commented out in the source (:272); never shipped.
//   · SUBMIT INVOICE — `invoiceActions()` exists (:967) but is NEVER CALLED; the real
//                      control lives in invoice_services_widget.dart:149.
//   · the mic        — `// audioButton()` is commented out in `menuOptions` (:517-521).
// Flutter's own order is ePrescription → Send orders → Sign note (sideWidgetOfHeader:277+).
//
// Replaces WrapUpStatusHeader AND the WrapUpSignLock section (retired in the same
// motion): Send-Orders and Sign keep their QA-verified handlers 1:1 — same gates,
// same disabled reasons (now surfaced as the icon-action tooltip). The big Sign & Lock
// button collapses into the SIGN NOTE cluster's pen action per the frame.
//
// Anti-fiction (chrome precedent, encounter-section-chrome.tsx): affordances with no
// live backend render VISIBLE but DISABLED, labelled "(coming soon)" — never wired to
// nothing: invoice preview/submit (PROD-1309), orders preview, mark-as-seen,
// ePrescription, save-note, and the trailing mic/edit/comment row.
//
// NUMERIC SPEC CONFORMANCE (touchpoint-3 reject, wrap-up.spec.json + raw codegen,
// node 1937:187863 "EncounterTitle" — Figma @1x):
//   row: ONE flex row, gap 16, items-center, justify-between (clusters left, mic/edit/
//        comment pinned right) → gap var(--spacing-4), alignItems center.
//   cluster (e.g. Submit Invoice w=113 h=40): label row [clock 12px at x0 · label at
//        x16 → 4px gap], icons row at y16 → 4px row gap; icons at x0 & x32 →
//        PITCH 32px, held by 32px EncounterSectionAction buttons at gap 0.
//   ICON INK — 20px, NOT 24 (corrected 2026-09-08, Ahmed: "the Sign note icons are
//        really big, make them same size as the deployed version"). The E2 spec node
//        above draws these at a 24 box and the chrome's own trailing seat is 24
//        (DS-ratified there, 1.50.2), but Wrap-Up is one of the six screens with no
//        Longevity Figma frame, so repo rule R9 ranks the Flutter WIDGET SOURCE first
//        and it is unambiguous: every cluster action is `size: 20`
//        (wrap_up_billing_screen.dart:325,357,416,436,486,503) while the SAME row's
//        trailing hand-notes/comment pair stays `size: 24`
//        (common_encounter_component_base_tab.dart:358,368). Two inks in one row is
//        the source's grammar. Carried by the DS `glyph={20}` prop — the 32px button
//        box, and so the pitch and the hit target, are unchanged at either ink.
//   label: Roboto Bold 10px / +0.1px / uppercase / legacy icons-grey → 10px EXACT
//        (no DS token that small — 11px --font-size-xs makes the row overflow 917px
//        and wrap; 10px literal kept, token flagged) / 700 / 0.1px /
//        --color-text-hint (the spec palette's own mapping for the legacy icons-grey).
//   trailing: three 24px icons, gap 16 → pitch 40 = 32px buttons at gap 8
//        (var(--spacing-2)).
//   step glyph: spec 12px — DS Icon minimum is 16 (sizes 16|20|24|32); 16 kept,
//        flagged (no CSS hacks on DS internals).
//
// 2026-09-22 (PA→Signal DS consumption): this file no longer hand-composes the row.
// It now maps the same three semantic slices (status/signLock/ePrescription) to the DS
// organism `WrapUpActionsHeader`'s generic `clusters`/`trailingActions` shape and renders
// that. The DS organism owns the chrome tile/title, the cluster anatomy, the 20px cluster
// ink / 24px trailing ink, and the placement fix below — this file owns only WHICH
// clusters exist and their state. See the DS-CAPABILITY-GAP comments in the component body
// for the two things that could not be preserved through that handoff (the eRx reference
// text run, and the Sign-note tri-state clock).
//
// Historical anatomy notes below (pre-DS-organism, kept for the numeric-spec conformance
// record — the DS organism now owns and is measured against these same numbers):
// DS-FIRST CANDIDATES — RESOLVED by DS 1.52 (cure adoptions, 2026-07-22):
//   • the wrap-up identity tile: the hand-composed green square is retired — the DS
//     `EncounterSectionChrome` now ships `kind='wrap-up'` (cure wave 5) whose seat IS the
//     frame's solid status-success Signoff tile (the one sanctioned status-colour identity).
//     The chrome also owns the "Wrap-Up" title — and that is ALL it carries here: the
//     clusters/trailing render as the row's own flex children, NOT in the chrome's
//     `actions` slot (that slot is a shrink-0 right-pinned toolbar for 3-4 icon buttons;
//     composing the clusters into it compressed them into the header's trailing corner —
//     Samer's live-screenshot placement reject, 2026-07-22).
//   • the 12px step glyph: DS Icon's size union gained 12 ("wrap-up step clock", 1.52) —
//     StepGlyph now renders spec-exact 12px (the flagged 16px compromise retired).
//   • SEND + PREVIEW glyphs: RESOLVED (DS wave 2 bridged `send: "resend"` and
//     `preview: "preview"` into SCRIPS_GLYPH) — this file now renders the TRUE Scrips
//     font glyphs via icon="send" / icon="preview" (wave 4 adoption; 'share'/'eye'
//     stand-ins retired).
//   • the SIGNATURE glyph: RESOLVED 2026-09-08 — `save` and the new `sign` name are
//     Scrips-font bridges in the DS (app_icons.dart iconSave 0xe97d / iconSign 0xe97e,
//     cmap-verified in Scrips.ttf), the same treatment send/preview got in 1.50.2. The
//     Lucide 'pen-line'/'save' stand-ins are retired here: their ink fills the 24-box
//     far heavier than the Scrips glyphs beside them, which is the other half of why
//     this pair read oversized next to PREVIEW and SEND at an identical box size.
import {
  WrapUpActionsHeader as DsWrapUpActionsHeader,
  type WrapUpActionCluster,
  type WrapUpTrailingAction,
} from '@scripsteam/scrips-signal-ds';
import { type SignLockState } from './lib/to-sign-lock';
import { signActionState } from './lib/sign-action-state';

/** Send-Orders + sign progress — the EXACT slice the encounter page already feeds
 *  (was WrapUpStatusHeader's props; the page wiring is unchanged). */
export interface WrapUpStatusProps {
  /** Orders have been sent (Flutter `apData.isOrderSent`) — flips the cluster ✓. */
  orderSent: boolean;
  /** Total orders across the encounter (nested per-Dx orders + referrals). */
  orderCount: number;
  /** Enable the Send-Orders action — Flutter `isDiagnosisAdded` (≥1 Dx). */
  canSendOrders: boolean;
  /** Re-save the A/P with the order-sent flag set (owned by the caller). */
  onSendOrders: () => void;
  /** A save is in flight — disables the action. */
  sendingOrders?: boolean;
  /** The encounter is signed (Flutter `isSignAndLocked`) — flips the Sign cluster ✓. */
  signed: boolean;
  /**
   * Preview the orders. Flutter opens `PostVisitSummaryStandalonePage` for the encounter
   * (wrap_up_billing_screen.dart:411-427) — the same artefact React renders through the
   * shipped PVS print path. Absent → the action stays disabled.
   */
  onPreviewOrders?: () => void;
  previewingOrders?: boolean;
}

/** Sign slice — the EXACT shape the encounter page already feeds (was WrapUpSignLockProps).
 *  Nurse → "Sign" (no lock); doctor → "Sign & Lock", diagnosis-gated (Flutter bloc rule). */
export interface WrapUpSignLockProps {
  state: SignLockState;
  /** Sourced from the real `isNurse` role field — nurse signs without locking. */
  isNurse: boolean;
  /**
   * Doctor sign is clear of the host's SAVE-IN-FLIGHT gates. The two CLINICAL gates (chief
   * complaint, diagnosis) are NOT here any more — Flutter fires those on click as toasts and
   * leaves the icon live, so the host does the same in `handleSign`.
   */
  canDoctorSign?: boolean;
  /** Why the doctor sign is disabled — surfaced as the action's tooltip. */
  signDisabledReason?: string;
  /**
   * Save the encounter — Flutter `b.saveEncounter(isExist: false)` (:487). LIVE and unconditional
   * there; the host flushes the debounced questionnaire write. Absent → the action falls back to
   * the disabled "(coming soon)" state, as it did before a save path was wired.
   */
  onSaveNote?: () => void;
  /** Rule 38 (Andrew's walk, 7 Oct, B10; ON and OFF): the PROVIDER has signed — nothing is saved in place any more.
   *  Save Encounter reads disabled (OFF) or is not drawn (frame v2). A nurse-only sign does not set it. */
  saveLocked?: boolean;
  /** Sign status couldn't be loaded — block signing (never double-sign an unknown state). */
  loadError?: boolean;
  signing?: boolean;
  /**
   * ROLE-NEUTRAL transient block (PROD-1690/Codex): an A&P save is in flight AND signing would
   * need the order-sign promotion write, which serializes behind it (one in-flight PUT). Unlike
   * `canDoctorSign` this also gates the NURSE sign — the page's onSign no-ops in this window, so
   * an enabled action would silently swallow the click.
   */
  saveInFlight?: boolean;
  /** PROD-3142 (D-78): a reason the page blocks every sign (doctor and nurse), shown as the button's title. Absent → none. */
  blockedReason?: string;
  /** The reason the LAST Sign click was refused (`?x=sign-reason`), drawn next to the Sign button. Absent → none. */
  refusedReason?: string;
  /**
   * What a Sign click would be refused for RIGHT NOW by the page's clinical gates (a chief complaint, then a diagnosis), in their
   * order and words. Read by the floating sign bar (`visitNoteV2`) only, so it never says "Ready to sign" before a refusal. The
   * Sign itself is unchanged: it stays live and refuses on click, as Flutter does. Absent → no gate is open.
   */
  clinicalGateReason?: string;
  onSign: () => void;
}

/* NO invoice cluster here. Flutter's wrap-up header has none — its `invoiceActions()`
   (wrap_up_billing_screen.dart:967) is dead code, never called; the live Create/Update-Invoice
   control is rendered by the invoice widget itself (invoice_services_widget.dart:149). The
   invoice add/preview affordances live on WrapUpInvoiceSection, where the source puts them. */

/**
 * ePrescription cluster (Flutter sideWidgetOfHeader:282-383). Absent → NO cluster (PROD-2946): an
 * ePrescription control is offered only when the host has evaluated `showRx` and said so. It used
 * to render (disabled) when absent, so a host that never wired eRx — or a persona that may not
 * prescribe — still showed a prescribing control.
 *
 * Flutter's own visibility rule (`showRx`, :265-269) is the HOST's to evaluate — it needs the
 * med list and the stale flag: eRx enabled AND (an existing eRx that has since changed OR ≥1
 * prescribed med OR ≥1 active med). The host passes the result as `visible`.
 */
export interface WrapUpEPrescriptionProps {
  /** Flutter `showRx` — false hides the cluster entirely. */
  visible: boolean;
  /** Flutter `isRxSynced` = an eRx exists AND the plan hasn't changed since → drives the ✓. */
  synced: boolean;
  /** The eRx reference, rendered as "eRx {ref}" beside the actions when one exists. */
  reference?: string | null;
  /** Send / re-send to DHPO. A re-send cancels the previous eRx — the HOST must confirm first
   *  (Flutter's confirm dialog, :358-373). Absent → the action stays disabled. */
  onSend?: () => void;
  sending?: boolean;
  /** Open the read-only prescription preview (Flutter `EPrescriptionPopUp`, :320-346). */
  onPreview?: () => void;
}

/**
 * PROD-2743 · frame v2 (DARK). The note-level actions that left the right column with the generated
 * note (chair ruling, 30 Sep): they live HERE, in the Wrap-up action bar beside Save / Sign, because
 * they are end-of-visit acts on the whole note. Supplied by the page only when the frame-v2 switch
 * is on; absent (the default) → the bar renders exactly as before.
 */
export interface WrapUpNoteActionsProps {
  /** Opens the generated note in the centre column. */
  onViewNote: () => void;
  /** Opens the Clinical Edit results (RunClinicalEdits) in the centre column. */
  onClinicalEdit: () => void;
  /** Opens the post-visit summary (Andrew's walk, 7 Oct: it had hidden behind "Preview orders"). */
  onViewPvs?: () => void;
  /** Opens the existing Discard-note confirm (the same handler the note footer's pill fired). Absent → not drawn:
   *  after the provider's sign, and never for a nurse (Andrew's walk, 7 Oct, B10 / D3). */
  onDiscard?: () => void;
  /** The note footer's own gate for Discard (a sign, its promotion or a PVS print in flight). */
  discardDisabled?: boolean;
}

export interface WrapUpActionsHeaderProps {
  status?: WrapUpStatusProps;
  signLock?: WrapUpSignLockProps;
  ePrescription?: WrapUpEPrescriptionProps;
  noteActions?: WrapUpNoteActionsProps;
  /**
   * TS-3 · frame v2: the step sits inside the note's own "Wrap-up" block, which already titles it. The
   * DS header's section chrome (the ✓ tile and "Wrap-Up") would say it a second time, in another
   * spelling, so it is not drawn (design desk, 5 Oct: each title once, the block's spelling). The action
   * clusters stay. signal-ds 2.20's `chrome={false}` (#454).
   */
  untitled?: boolean;
  /**
   * Frame v2 only: the bar sits inside the note's Wrap-up block (the 6 Oct audit).
   *  · FV2-05 — no Sign quill: signing is the labelled "Sign note" / "Sign as nurse" button in the note
   *    header (R6c Encounter Frame), wired to the same `signLock.onSign` and the same gates
   *    (`signActionState`).
   *  · FV2-06 — no "coming soon": an action with no handler is not drawn (D-60, a gap is never a
   *    placeholder), and a cluster left with no action is not drawn either. A live action that is
   *    only disabled for now (Send orders before a diagnosis, a send in flight) stays, with its reason.
   * Absent (OFF, and the old frame) → the bar renders exactly as before.
   */
  inNote?: boolean;
  /**
   * The long page (22 Oct, Antonin's review): the note actions and Send orders are drawn as labelled buttons in the page's Wrap-up
   * foot (`note-sign-actions.tsx`), so this bar leaves them out — every other action and cluster is unchanged. Only with `inNote`.
   */
  footActions?: boolean;
  /**
   * The foot really draws a Send orders button (the page's `footSendOrders`). The bar leaves its own Send orders out only then,
   * so a foot without one (no Wrap-up status yet) never takes the action away. Only with `inNote` and `footActions`.
   */
  footSendOrders?: boolean;
}

/** Send orders' state — the QA-verified WrapUpStatusHeader gates, 1:1 — shared by this bar's action and the long page's foot. */
export function sendOrdersState(status: WrapUpStatusProps | undefined): { disabled: boolean; title: string } {
  const disabled = !status || !status.canSendOrders || status.orderSent || !!status.sendingOrders;
  const title = !status
    ? 'Send orders'
    : status.sendingOrders
      ? 'Sending…'
      : status.orderSent
        ? 'Orders sent'
        : status.canSendOrders
          ? 'Send orders'
          : 'Add a diagnosis in Assessment & Plan before sending orders.';
  return { disabled, title };
}

/** The foot's Send orders label: the count in the words, never an icon alone. */
export function sendOrdersLabel(status: WrapUpStatusProps): string {
  if (status.sendingOrders) return 'Sending…';
  if (status.orderSent) return 'Orders sent';
  return status.orderCount > 0 ? `Send orders (${status.orderCount})` : 'Send orders';
}

/** Frame v2 (`inNote`): keep only the actions that DO something (a handler exists). */
function liveOnly<T extends { onClick?: () => void }>(actions: T[]): T[] {
  return actions.filter((a) => a.onClick !== undefined);
}


// The trailing row — Flutter `menuOptions` (wrap_up_billing_screen.dart:517-521) is exactly
// TWO: handwritten-notes and comment ("Add Patient Instructions"); the audio/dictate button
// above them is COMMENTED OUT (`// audioButton()`), so the mic that used to sit here had no
// counterpart in the source and is removed. Both write patient instructions; no wrap-up-level
// handler is fed yet, so they ship disabled per the chrome anti-fiction precedent.
const TRAILING_ACTIONS: WrapUpTrailingAction[] = [
  { id: 'edit', icon: 'edit', label: 'Handwritten notes (coming soon)', disabled: true },
  { id: 'message', icon: 'message', label: 'Add Patient Instructions (coming soon)', disabled: true },
];

// Named `EncounterWrapUpActionsHeader`, NOT `WrapUpActionsHeader` — the DS package now exports
// its OWN `WrapUpActionsHeader` (imported above as `DsWrapUpActionsHeader`), and this function
// still does real translation work (status/signLock/ePrescription → the DS's generic
// clusters/trailingActions shape), so it is not a pure re-export facade. Reusing the DS's exact
// name here would be flagged as a reimplementation by `check-ds-shadows.mjs` — correctly: this
// component is genuinely a DIFFERENT thing (a semantic-prop adapter), not the DS organism itself.
export function EncounterWrapUpActionsHeader({ status, signLock, ePrescription, noteActions, untitled, inNote, footActions, footSendOrders }: WrapUpActionsHeaderProps) {
  // The long page draws these in its foot (`footActions`): not here as well.
  const inFoot = !!inNote && !!footActions;
  // Send orders leaves the bar only when the foot really has it (#764 review fix).
  const sendInFoot = inFoot && !!footSendOrders;
  // Sign action state (the QA-verified WrapUpSignLock gates, 1:1):
  const locked = signLock?.state === 'locked';
  const { disabled: signDisabled, title: signTitle } = signActionState(signLock);

  // Send-Orders action state (the QA-verified WrapUpStatusHeader gates, 1:1):
  const { disabled: sendDisabled, title: sendTitle } = sendOrdersState(status);

  // Composes the DS `WrapUpActionsHeader` organism (chrome tile/title + cluster list +
  // trailing icons all owned by the DS component now — this file only computes WHICH
  // clusters/actions exist and what state they're in, exactly as it did before).
  //
  // DS CAPABILITY RESTORED (2026-09-22): `WrapUpActionCluster.status` grew a third state,
  // `'partial'` (a success-coloured clock, distinct from the muted `'pending'` clock and the
  // `'done'` check) — see `WrapUpActionsHeader.d.ts`. This closes the regression: Flutter's
  // Sign-note step glyph is genuinely tri-state (wrap_up_billing_screen.dart:462-468) —
  //   done    ← isSignAndLocked && isOrderSent   (tick)
  //   partial ← isSignAndLocked alone            (green clock — signed, orders never sent)
  //   pending ← neither                          (muted clock)
  // `isSignedAndLocked` below mirrors Flutter's `isSignAndLocked` (the same fallback to the
  // local `locked` flag the tick condition already used); the sign-note cluster now maps all
  // three, so a doctor who has signed but never sent orders shows the green clock instead of
  // being indistinguishable from "nothing done yet".
  const clusters: WrapUpActionCluster[] = [];

  // Flutter gates the whole cluster on `showRx` (:282) — the host evaluates it and passes
  // `visible`. Icon order is Flutter's: PREVIEW first (:320), then resend/send (:350). The ✓
  // flips on `isRxSynced` (:291-295).
  // PROD-2946: hidden unless the host says visible. Absent is NOT visible.
  if (ePrescription?.visible ?? false) {
    clusters.push({
      id: 'eprescription',
      // DS CAPABILITY GAP: Flutter renders "eRx {id}" as its OWN 13px/w500/brand-blue run
      // before the actions (wrap_up_billing_screen.dart:308-319); the DS cluster has no slot
      // for a second text run, only `label`. Folded into the label so the information still
      // surfaces (not silently dropped) — the distinct type/colour treatment is lost.
      label: ePrescription?.reference ? `ePrescription — eRx ${ePrescription.reference}` : 'ePrescription',
      status: ePrescription?.synced ? 'done' : 'pending',
      actions: [
        {
          icon: 'preview',
          label: ePrescription?.onPreview ? 'Preview ePrescription' : 'Preview ePrescription (coming soon)',
          disabled: !ePrescription?.onPreview,
          onClick: ePrescription?.onPreview,
        },
        {
          icon: 'send',
          label: !ePrescription?.onSend
            ? 'Send ePrescription (coming soon)'
            : ePrescription.sending
              ? 'Sending…'
              : ePrescription.reference
                ? 'Resend ePrescription'
                : 'Send ePrescription',
          disabled: !ePrescription?.onSend || !!ePrescription?.sending,
          onClick: ePrescription?.onSend,
        },
      ],
    });
  }

  // Flutter renders this cluster only for non-nurses (`if (!b.isNurse)`, :384).
  if (!signLock?.isNurse) {
    clusters.push({
      id: 'send-orders',
      label: 'Send orders',
      count: status?.orderCount,
      status: status?.orderSent ? 'done' : 'pending',
      actions: [
        {
          // Flutter opens the Post-Visit Summary for the encounter (:411-427); React renders
          // the same artefact via the shipped PVS print path.
          icon: 'preview',
          label: !status?.onPreviewOrders
            ? 'Preview orders (coming soon)'
            : status.previewingOrders
              ? 'Preparing…'
              : 'Preview orders',
          disabled: !status?.onPreviewOrders || !!status?.previewingOrders,
          onClick: status?.onPreviewOrders,
        },
        // The long page: Send orders is the foot's labelled button (`footSendOrders`).
        ...(sendInFoot
          ? []
          : [
              {
                // Flutter swaps the glyph once orders are sent: edit (jump back to A/P) vs resend
                // (send now) — `isOrderSent ? ScripsIcons.edit : ScripsIcons.resend` (:434).
                icon: status?.orderSent ? ('edit' as const) : ('send' as const),
                label: sendTitle,
                disabled: sendDisabled,
                onClick: status?.onSendOrders,
              },
            ]),
      ],
    });
  }

  // Flutter's `isSignAndLocked` — same source the tick condition always used, kept as one
  // computation so `done`/`partial` cannot disagree about what "signed" means.
  const isSignedAndLocked = status?.signed ?? locked;
  clusters.push({
    id: 'sign-note',
    label: 'Sign note',
    // See the DS-capability note above — all three of Flutter's conditions, now expressible.
    status: isSignedAndLocked ? (status?.orderSent ? 'done' : 'partial') : 'pending',
    actions: [
      // Frame v2 only: the note-level actions, before Save / Sign (see WrapUpNoteActionsProps).
      ...(noteActions && !inFoot
        ? [
            { icon: 'file' as const, label: 'View generated note', onClick: noteActions.onViewNote },
            { icon: 'clipboard' as const, label: 'Clinical edits', onClick: noteActions.onClinicalEdit },
            ...(noteActions.onViewPvs ? [{ icon: 'preview' as const, label: 'Post-visit summary', onClick: noteActions.onViewPvs }] : []),
            ...(noteActions.onDiscard
              ? [
                  {
                    icon: 'delete' as const,
                    label: 'Discard This Note',
                    onClick: noteActions.onDiscard,
                    disabled: !!noteActions.discardDisabled,
                  },
                ]
              : []),
          ]
        : []),
      {
        // Flutter's tooltip verbatim — `CircularTooltip(message: 'Save Encounter')` (:482). The
        // SIGN action keeps "Sign Note" rather than Flutter's "Sign Encounter" tooltip, because
        // that label is Andrew's explicit 2026-08-27 naming ruling (`S.signNote`) and is pinned
        // by six assertions; this one had no such ruling to keep — it was a placeholder.
        icon: 'save',
        label: signLock?.onSaveNote ? 'Save Encounter' : 'Save note (coming soon)',
        disabled: !signLock?.onSaveNote || !!signLock?.saveLocked,
        // Frame v2 drops an action with no handler (FV2-06), so a locked Save is simply not drawn there.
        onClick: signLock?.saveLocked ? undefined : signLock?.onSaveNote,
      },
      // Frame v2: signing is the labelled button in the note header (FV2-05), not this quill.
      ...(inNote
        ? []
        : [
            {
              // The REAL signature glyph — DS `sign` is the Scrips-font bridge of Flutter's
              // `ScripsIcons.iconSign` (0xe97e, app_icons.dart:148), drawn at
              // wrap_up_billing_screen.dart:503.
              icon: 'sign' as const,
              label: signTitle,
              disabled: signDisabled,
              onClick: signLock?.onSign,
            },
          ]),
    ],
  });

  // titleTone="depth" — the encounter section titles are brand-DEPTH by ruling, and this is
  // the seventh and last tab to get there by prop rather than by a CSS reach-in. Wrap-up
  // needed a DS pass-through because the chrome is composed INSIDE the organism.
  //
  // ⚠️ RULING STILL OWED, carried forward from the override file's deleted §28 comment so it
  // is not lost with it: Flutter draws these headings boldLabelTextStyle(17,
  // defaultFieldTextColor) (image_header_widget.dart:31), and defaultFieldTextColor
  // (app_colors.dart:64) is the legacy title blue, whose only recorded substitution to
  // --color-brand-depth sits inside FREEZE-MANIFEST's DocumentRow entry (:280) — a
  // per-component anatomy note, NOT a palette licence. Moving the colour from CSS to this
  // prop preserves the rendered result exactly and settles nothing. Samer rules on the
  // Docs and A/P seats together, or both revert together.
  // Frame v2: drop every action with no handler, then every cluster left with none (FV2-06). The two
  // trailing actions have no wrap-up-level handler at all, so none is drawn.
  const shown = inNote
    ? clusters.map((c) => ({ ...c, actions: liveOnly(c.actions) })).filter((c) => c.actions.length > 0)
    : clusters;
  return (
    <DsWrapUpActionsHeader
      clusters={shown}
      trailingActions={inNote ? liveOnly(TRAILING_ACTIONS) : TRAILING_ACTIONS}
      titleTone="depth"
      // signal-ds 2.20 (#454) `chrome={false}`: no tile and no "Wrap-Up" title inside the note's block, which
      // already titles it. Replaces the `[&>section]:hidden` class the class guard refuses. OFF: no key.
      {...(untitled ? { chrome: false } : {})}
      // Frame v2 inside the note (signal-ds 2.20, FV2-02): the clusters' step captions in text-secondary (AA).
      {...(inNote ? { labelTone: 'secondary' as const } : {})}
    />
  );
}
